import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Landing.css';
import { facilitySettingsAPI } from '../BaseURL';
import { IS_ORIGINAL_PHOTO_SITE, originalPhotosFromSettings } from '../../utils/facilityPhotos';

/* ─────────────────────────────────────────────────────────────────────
   Landing page — public marketing site for the diagnostics facility.
   Self-contained: imports its own CSS and uses no MUI components, so it
   stays visually independent from the app's MUI theme.

   Copy here is placeholder. Replace each section's strings with the
   facility's actual content. Images are served from /pic/* in public/.
   ───────────────────────────────────────────────────────────────────── */

const PUBLIC = process.env.PUBLIC_URL || '';
const IMG = (name) => `${PUBLIC}/pic/${name}`;
const DEFAULT_IMG = (name) => `${PUBLIC}/login-defaults/${name}.svg`;

// ── Photo sets ────────────────────────────────────────────────────────
// The /pic photos were taken at Dekyi; every other facility gets the
// neutral illustrations shared with the login page. Which set shows is
// decided by utils/facilityPhotos (same rule as the login carousel).
// The illustrations keep their artwork on the right, so they're anchored
// right when cropped into the portrait hero / card frames.
const ORIGINAL_PHOTOS = {
  hero:      ['pic1.jpeg','pic2.jpeg','pic9.jpeg','pic10.jpeg','pic11.jpeg','pic12.jpeg'].map(IMG),
  services:  ['pic3.jpeg','pic4.jpeg','pic5.jpeg'].map(IMG),
  about:     IMG('pic6.jpeg'),
  reception: IMG('pic7.jpeg'),
  style:     undefined,
};
const DEFAULT_PHOTOS = {
  hero:      ['diagnostics','laboratory','results','care','patient','workflow'].map(DEFAULT_IMG),
  services:  ['results','diagnostics','patient'].map(DEFAULT_IMG),
  about:     DEFAULT_IMG('care'),
  reception: DEFAULT_IMG('workflow'),
  style:     { objectPosition: 'right center' },
};

// ── Inline icons (Lucide-style stroke set) ────────────────────────────
const ICON_PATHS = {
  services: <><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></>,
  tests:    <><path d="M9 2v7L4 17a2 2 0 0 0 2 3h12a2 2 0 0 0 2-3L15 9V2"/><path d="M9 2h6"/><path d="M7 14h10"/></>,
  about:    <><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></>,
  faq:      <><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></>,
  location: <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></>,
  partners: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
  contact:  <><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></>,
  signin:   <><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10,17 15,12 10,7"/><line x1="15" y1="12" x2="3" y2="12"/></>,
  // Pointing-hand glyph used to draw the eye toward the Sign-In button. The
  // wrist + index-finger silhouette is built from a single path so the
  // surrounding pulse animation stays cheap (no per-finger transforms).
  hand:     <><path d="M9 11V6a2 2 0 1 1 4 0v5"/><path d="M13 11V4a2 2 0 1 1 4 0v7"/><path d="M17 11V6a2 2 0 1 1 4 0v9a7 7 0 0 1-7 7h-2a8 8 0 0 1-8-8v-1a2 2 0 1 1 4 0"/><path d="M9 11V9a2 2 0 1 0-4 0v5"/></>,
  calendar: <><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>,
  search:   <><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></>,
  arrow:    <><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12,5 19,12 12,19"/></>,
  close:    <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>,
  phone:    <><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></>,
  clock:    <><circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/></>,
};
const Icon = ({ name, size = 16, style }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0, ...style }}
  >
    {ICON_PATHS[name] || null}
  </svg>
);

// ── Data (edit these to swap copy without touching JSX) ───────────────
// Service card images come from the active photo set, by position.
const services = [
  {
    tag: 'Diagnostics',
    title: 'Comprehensive Laboratory Testing',
    desc: 'Full-spectrum blood, urine and tissue analysis powered by modern automated analyzers and qualified scientists.',
  },
  {
    tag: 'Imaging',
    title: 'Advanced Medical Imaging',
    desc: 'High-resolution ultrasound, X-ray and other imaging studies interpreted by experienced radiologists.',
  },
  {
    tag: 'Wellness',
    title: 'Executive Health Screening',
    desc: 'Curated screening packages designed to detect risk factors early and keep you in optimal health.',
  },
];

const labTests = [
  { code: 'CBC', title: 'Full Blood Count',        sub: 'Hematology' },
  { code: 'LFT', title: 'Liver Function Test',     sub: 'Biochemistry' },
  { code: 'KFT', title: 'Kidney Function Test',    sub: 'Biochemistry' },
  { code: 'LP',  title: 'Lipid Profile',           sub: 'Cardiac risk' },
  { code: 'HBA', title: 'HbA1c (Diabetes)',        sub: 'Endocrinology' },
  { code: 'TFT', title: 'Thyroid Function Test',   sub: 'Endocrinology' },
  { code: 'UA',  title: 'Urinalysis',              sub: 'General' },
  { code: 'STI', title: 'STI Screening Panel',     sub: 'Sexual health' },
];

const faqs = [
  {
    q: 'Do I need an appointment to visit the laboratory?',
    a: 'Walk-ins are welcome, but booking an appointment shortens your wait time and lets us prepare for any special tests in advance.',
  },
  {
    q: 'How long do results take?',
    a: 'Most routine results are available within 24 hours. Specialised panels may take 2–5 working days; turnaround time is shown on each test page.',
  },
  {
    q: 'Can I receive my results online?',
    a: 'Yes. Once your tests are validated, you receive a secure link via email or SMS to view and download your report.',
  },
  {
    q: 'Do you accept health insurance?',
    a: 'We partner with several major health insurance schemes. Please contact our reception with your policy details to confirm coverage.',
  },
  {
    q: 'Is fasting required before a blood test?',
    a: 'Some tests (e.g. lipid profile, fasting glucose) require 8–12 hours of fasting. Instructions are sent to you when you book.',
  },
];

const partners = ['Partner One', 'Partner Two', 'Partner Three', 'Partner Four', 'Partner Five', 'Partner Six'];

// ─────────────────────────────────────────────────────────────────────
export default function Landing() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(0);
  const [slide, setSlide] = useState(0);
  const [trackOpen, setTrackOpen] = useState(false);
  const [trackCode, setTrackCode] = useState('');
  const [trackMsg, setTrackMsg] = useState('');

  // Facility logo + name — sourced from the `companyInfo` cache that App.js
  // refreshes from /companyInfo on every boot. Seeded synchronously so the
  // first paint already shows the real brand instead of the placeholder.
  const [company, setCompany] = useState(() => {
    try {
      const raw = localStorage.getItem('companyInfo');
      return raw ? JSON.parse(raw) : null;
    } catch (_) { return null; }
  });
  useEffect(() => {
    // Re-read when another tab refreshes the cache (e.g. an admin updates
    // the facility name from Hospital Admin Settings while the landing page
    // is open). Same key the rest of the app writes to.
    const onStorage = (e) => {
      if (e.key !== 'companyInfo') return;
      try { setCompany(e.newValue ? JSON.parse(e.newValue) : null); } catch (_) {}
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Which photo set to show. Seeded from the build so Dekyi's photos (or the
  // defaults elsewhere) paint immediately; the facility setting can flip it.
  const [photos, setPhotos] = useState(() => (IS_ORIGINAL_PHOTO_SITE ? ORIGINAL_PHOTOS : DEFAULT_PHOTOS));
  useEffect(() => {
    let alive = true;
    axios.get(facilitySettingsAPI, { timeout: 8000 })
      .then(({ data }) => {
        if (alive) setPhotos(originalPhotosFromSettings(data) ? ORIGINAL_PHOTOS : DEFAULT_PHOTOS);
      })
      .catch(() => { /* keep the build default */ });
    return () => { alive = false; };
  }, []);
  const heroImages = photos.hero;

  const facilityName = (company && (company.name || '').trim()) || 'Diagnostics Center';
  const facilityLogo = company && company.logo;
  const brandInitial = String(facilityName)[0]?.toUpperCase() || 'D';

  // Smooth-scroll for in-page nav links
  useEffect(() => {
    const handler = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute('href').slice(1);
      const el = id && document.getElementById(id);
      if (el) { e.preventDefault(); el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // Hero auto-advance
  useEffect(() => {
    const id = setInterval(() => setSlide(s => (s + 1) % heroImages.length), 4500);
    return () => clearInterval(id);
  }, []);

  const goLogin = () => navigate('/login');
  const openTrack = () => { setTrackCode(''); setTrackMsg(''); setTrackOpen(true); };
  const submitTrack = (e) => {
    e.preventDefault();
    const code = trackCode.trim();
    if (!code) { setTrackMsg('Please enter your tracking code.'); return; }
    setTrackMsg(`No record found for code "${code}". Please double-check or contact our reception.`);
  };

  return (
    <div className="lp-root">
      {/* ── NAV ─────────────────────────────────────────────────────── */}
      <nav className="lp-nav">
        <div className="lp-container lp-nav-inner">
          <a href="#top" className="lp-brand">
            <span className="lp-brand-mark">
              {facilityLogo
                ? <img src={facilityLogo} alt={facilityName} />
                : brandInitial}
            </span>
            <span className="lp-brand-name">{facilityName}</span>
          </a>
          <div className="lp-nav-links">
            <a href="#services">Services</a>
            <a href="#tests">Lab Tests</a>
            <a href="#about">About</a>
            <a href="#faq">FAQ</a>
            <a href="#location">Location</a>
            <a href="#partners">Partners</a>
            <a href="#contact">Contact</a>
          </div>
          <div className="lp-nav-actions">
            <a href="#contact" className="lp-btn lp-btn-ghost"><Icon name="calendar" />Book Appointment</a>
            <button className="lp-btn lp-btn-outline-accent" onClick={openTrack}><Icon name="search" />Track Results</button>

            {/* Sign-In CTA — wrapped so we can layer a pointing-hand glyph and
                a soft ring pulse to direct first-time visitors. The hand bobs
                horizontally toward the button; the ring breathes around it. */}
            <span className="lp-signin-wrap">
              <span className="lp-signin-hint" aria-hidden="true">
                <Icon name="hand" size={26} />
              </span>
              <span className="lp-signin-pulse" aria-hidden="true" />
              <button
                className="lp-btn lp-btn-primary lp-btn-signin"
                onClick={goLogin}
              >
                <Icon name="signin" />Sign In
              </button>
            </span>
          </div>
        </div>
      </nav>

      {/* ── HERO ────────────────────────────────────────────────────── */}
      <header id="top" className="lp-hero">
        <div className="lp-container lp-hero-grid">
          <div>
            <span className="lp-hero-eyebrow">
              <span style={{width:6,height:6,borderRadius:'50%',background:'#22c55e',display:'inline-block'}} />
              Accepting new patients
            </span>
            <h1>
              Modern diagnostics. <br/>
              <span className="lp-accent">Accurate results,</span> faster care.
            </h1>
            <p className="lp-lead">
              We combine certified laboratory science with advanced imaging
              to deliver reliable answers — so you and your clinician can act
              with confidence.
            </p>
            <div className="lp-hero-cta">
              <a href="#contact" className="lp-btn lp-btn-primary"><Icon name="calendar" />Book Appointment<Icon name="arrow" /></a>
              <button className="lp-btn lp-btn-ghost" onClick={openTrack}><Icon name="search" />Track Results</button>
            </div>
            <div className="lp-hero-stats">
              <div>
                <div className="lp-stat-num">250+</div>
                <div className="lp-stat-label">Tests offered</div>
              </div>
              <div>
                <div className="lp-stat-num">24h</div>
                <div className="lp-stat-label">Routine turnaround</div>
              </div>
              <div>
                <div className="lp-stat-num">ISO</div>
                <div className="lp-stat-label">Quality standards</div>
              </div>
            </div>
          </div>

          <div className="lp-hero-visual">
            <div className="lp-slider">
              {heroImages.map((src, i) => (
                <img
                  key={src}
                  src={src}
                  style={photos.style}
                  alt=""
                  className={`lp-slide${i === slide ? ' is-active' : ''}`}
                />
              ))}
            </div>

            <div className="lp-slider-arrows">
              <button
                aria-label="Previous slide"
                className="lp-slider-arrow"
                onClick={() => setSlide(s => (s - 1 + heroImages.length) % heroImages.length)}
              >‹</button>
              <button
                aria-label="Next slide"
                className="lp-slider-arrow"
                onClick={() => setSlide(s => (s + 1) % heroImages.length)}
              >›</button>
            </div>

            <div className="lp-slider-dots">
              {heroImages.map((_, i) => (
                <button
                  key={i}
                  aria-label={`Go to slide ${i + 1}`}
                  className={`lp-slider-dot${i === slide ? ' is-active' : ''}`}
                  onClick={() => setSlide(i)}
                />
              ))}
            </div>

            <div className="lp-hero-visual-overlay">
              <span className="dot" />
              <div>
                <div className="label">Lab open now</div>
                <div className="sub">Mon–Sat · 7:00 AM – 7:00 PM</div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ── SERVICES ────────────────────────────────────────────────── */}
      <section id="services" className="lp-section">
        <div className="lp-container">
          <div className="lp-section-head">
            <span className="lp-section-eyebrow"><Icon name="services" size={13} />Our Services</span>
            <h2>Everything you need under one roof</h2>
            <p>From routine screening to advanced diagnostics, our integrated services help your doctor reach the right decisions, sooner.</p>
          </div>
          <div className="lp-grid-3">
            {services.map((s, i) => (
              <article key={i} className="lp-card">
                <div className="lp-card-media"><img src={photos.services[i % photos.services.length]} style={photos.style} alt={s.title} /></div>
                <div className="lp-card-body">
                  <div className="lp-card-tag">{s.tag}</div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── LAB TESTS ───────────────────────────────────────────────── */}
      <section id="tests" className="lp-section lp-section-alt">
        <div className="lp-container">
          <div className="lp-section-head">
            <span className="lp-section-eyebrow"><Icon name="tests" size={13} />Lab Tests</span>
            <h2>Popular tests and panels</h2>
            <p>A snapshot of the most requested investigations. Our full menu covers hematology, biochemistry, microbiology, serology, and more.</p>
          </div>
          <div className="lp-grid-4">
            {labTests.map((t, i) => (
              <div key={i} className="lp-tile">
                <div className="lp-tile-icon">{t.code}</div>
                <div>
                  <div className="lp-tile-title">{t.title}</div>
                  <div className="lp-tile-sub">{t.sub}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{textAlign:'center', marginTop: 40}}>
            <a href="#contact" className="lp-btn lp-btn-outline-accent">Request a test not listed here →</a>
          </div>
        </div>
      </section>

      {/* ── ABOUT ───────────────────────────────────────────────────── */}
      <section id="about" className="lp-section">
        <div className="lp-container">
          <div className="lp-grid-2 lp-about">
            <div className="lp-about-image"><img src={photos.about} style={photos.style} alt="About the facility" /></div>
            <div>
              <span className="lp-section-eyebrow"><Icon name="about" size={13} />About Us</span>
              <h2>Built on accuracy, integrity, and care</h2>
              <p>
                Our laboratory pairs state-of-the-art analyzers with experienced
                scientists and clinicians. Every sample passes a documented
                quality workflow before a result leaves our doors.
              </p>
              <p>
                We exist to give patients and referring clinicians answers they
                can trust — and to make the experience of getting tested as
                simple and dignified as possible.
              </p>
              <ul className="lp-about-list">
                <li>Internationally recognised quality controls</li>
                <li>Certified medical laboratory scientists</li>
                <li>Digital reports with secure delivery</li>
                <li>Friendly, patient-first reception</li>
                <li>Insurance-friendly billing</li>
                <li>Modern, comfortable facility</li>
              </ul>
              <a href="#contact" className="lp-btn lp-btn-primary">Visit us →</a>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────── */}
      <section id="faq" className="lp-section lp-section-alt">
        <div className="lp-container">
          <div className="lp-section-head">
            <span className="lp-section-eyebrow"><Icon name="faq" size={13} />FAQ</span>
            <h2>Answers to common questions</h2>
            <p>Still unsure about something? Reach out — our team is happy to help.</p>
          </div>
          <div className="lp-faq">
            {faqs.map((f, i) => (
              <div key={i} className={`lp-faq-item${openFaq === i ? ' open' : ''}`}>
                <button className="lp-faq-q" onClick={() => setOpenFaq(openFaq === i ? -1 : i)}>
                  <span>{f.q}</span>
                  <span className="lp-chev">+</span>
                </button>
                <div className="lp-faq-a">{f.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── LOCATION ────────────────────────────────────────────────── */}
      <section id="location" className="lp-section">
        <div className="lp-container">
          <div className="lp-section-head">
            <span className="lp-section-eyebrow"><Icon name="location" size={13} />Find Us</span>
            <h2>Visit our diagnostics center</h2>
            <p>Convenient location, easy parking, and friendly staff ready to receive you.</p>
          </div>
          <div className="lp-location">
            <div className="lp-map">
              <iframe
                title="Facility location"
                src="https://www.openstreetmap.org/export/embed.html?bbox=-0.30%2C5.55%2C-0.10%2C5.70&layer=mapnik"
                loading="lazy"
              />
            </div>
            <div className="lp-location-info">
              <div className="lp-info-row">
                <div className="lp-info-icon">📍</div>
                <div>
                  <strong>Address</strong>
                  <span>Add your full street address, suburb, and city here.</span>
                </div>
              </div>
              <div className="lp-info-row">
                <div className="lp-info-icon">📞</div>
                <div>
                  <strong>Phone</strong>
                  <span>+000 000 0000 · +000 000 0000</span>
                </div>
              </div>
              <div className="lp-info-row">
                <div className="lp-info-icon">✉️</div>
                <div>
                  <strong>Email</strong>
                  <span>hello@yourdomain.com</span>
                </div>
              </div>
              <div className="lp-info-row">
                <div className="lp-info-icon">🕒</div>
                <div>
                  <strong>Opening Hours</strong>
                  <span>Mon – Sat: 7:00 AM – 7:00 PM<br/>Sun: 9:00 AM – 2:00 PM</span>
                </div>
              </div>
              <a href="#contact" className="lp-btn lp-btn-primary" style={{ alignSelf: 'flex-start' }}>
                Book Appointment →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── PARTNERS ────────────────────────────────────────────────── */}
      <section id="partners" className="lp-section lp-section-alt">
        <div className="lp-container">
          <div className="lp-section-head">
            <span className="lp-section-eyebrow"><Icon name="partners" size={13} />Partners</span>
            <h2>Trusted by leading institutions</h2>
            <p>We collaborate with hospitals, clinics, and insurance schemes to deliver consistent quality.</p>
          </div>
          <div className="lp-partners">
            {partners.map((p, i) => (<div key={i} className="lp-partner">{p}</div>))}
          </div>
        </div>
      </section>

      {/* ── CONTACT ─────────────────────────────────────────────────── */}
      <section id="contact" className="lp-section">
        <div className="lp-container">
          <div className="lp-section-head">
            <span className="lp-section-eyebrow"><Icon name="contact" size={13} />Contact</span>
            <h2>Book an appointment or ask a question</h2>
            <p>Fill out the form and our team will get back to you within one working day.</p>
          </div>
          <div className="lp-grid-2" style={{gap: 28}}>
            <div className="lp-contact-form">
              <div className="lp-form-row">
                <input className="lp-input" type="text" placeholder="Full name" />
                <input className="lp-input" type="tel" placeholder="Phone number" />
              </div>
              <div className="lp-form-row lp-full">
                <input className="lp-input" type="email" placeholder="Email address" />
              </div>
              <div className="lp-form-row lp-full">
                <input className="lp-input" type="text" placeholder="Preferred test / service" />
              </div>
              <div className="lp-form-row lp-full">
                <textarea className="lp-textarea" placeholder="Tell us a bit more (optional)" />
              </div>
              <button
                className="lp-btn lp-btn-primary"
                style={{ width: '100%', justifyContent: 'center', padding: '14px' }}
                onClick={(e) => { e.preventDefault(); alert('Form submission is a placeholder. Wire this to your backend endpoint.'); }}
              >
                Send Request
              </button>
            </div>
            <div>
              <div className="lp-about-image" style={{ aspectRatio: '4 / 5' }}>
                <img src={photos.reception} style={photos.style} alt="Reception area" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ──────────────────────────────────────────────────── */}
      <footer className="lp-footer">
        <div className="lp-container">
          <div className="lp-footer-grid">
            <div>
              <a href="#top" className="lp-brand" style={{marginBottom: 14}}>
                <span className="lp-brand-mark">
                  {facilityLogo
                    ? <img src={facilityLogo} alt={facilityName} />
                    : brandInitial}
                </span>
                <span className="lp-brand-name">{facilityName}</span>
              </a>
              <p style={{fontSize: 14, lineHeight: 1.65, marginTop: 14, maxWidth: 320}}>
                Reliable laboratory and imaging services delivered with care,
                precision, and respect for every patient.
              </p>
            </div>
            <div>
              <h4>Services</h4>
              <ul>
                <li><a href="#services">Laboratory Testing</a></li>
                <li><a href="#services">Medical Imaging</a></li>
                <li><a href="#services">Health Screening</a></li>
                <li><a href="#tests">All Lab Tests</a></li>
              </ul>
            </div>
            <div>
              <h4>Company</h4>
              <ul>
                <li><a href="#about">About Us</a></li>
                <li><a href="#faq">FAQ</a></li>
                <li><a href="#location">Location</a></li>
                <li><a href="#partners">Partners</a></li>
              </ul>
            </div>
            <div>
              <h4>For Patients</h4>
              <ul>
                <li><a href="#contact">Book Appointment</a></li>
                <li><a href="/login">Track Results</a></li>
                <li><a href="/login">Sign In</a></li>
                <li><a href="#contact">Contact</a></li>
              </ul>
            </div>
          </div>
          <div className="lp-footer-bottom">
            <span>© {new Date().getFullYear()} {facilityName}. All rights reserved.</span>
            <span>Powered by Solute Cyber Technology Ltd</span>
          </div>
        </div>
      </footer>

      {/* ── Track Results modal (stays on landing) ─────────────────── */}
      {trackOpen && (
        <div className="lp-modal-backdrop" onClick={() => setTrackOpen(false)}>
          <div className="lp-modal" onClick={e => e.stopPropagation()}>
            <button
              className="lp-modal-close"
              aria-label="Close"
              onClick={() => setTrackOpen(false)}
            >
              <Icon name="close" size={18} />
            </button>
            <div className="lp-modal-icon"><Icon name="search" size={22} /></div>
            <h3 className="lp-modal-title">Track Your Results</h3>
            <p className="lp-modal-sub">
              Enter the tracking code we sent to your phone or email when your
              sample was received.
            </p>
            <form onSubmit={submitTrack}>
              <input
                className="lp-input"
                type="text"
                placeholder="e.g. DKY-2026-00123"
                value={trackCode}
                onChange={e => setTrackCode(e.target.value)}
                autoFocus
              />
              <button type="submit" className="lp-btn lp-btn-primary lp-modal-submit">
                <Icon name="search" />Check Status
              </button>
            </form>
            {trackMsg && <div className="lp-modal-msg">{trackMsg}</div>}
            <div className="lp-modal-hint">
              Lost your code? Call us at <strong>+000 000 0000</strong> or visit our reception.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
